import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Snackbar } from "@mui/material";
import { hd, errorMessage } from "./api";
import { getAgentSocket, useSocketEvent } from "./socket";
import { PageShell, T } from "../user/complaints/shared";
import TopBar from "./TopBar";

// Workspace metadata (statuses, priorities, categories, teams, agents, custom
// fields, permissions) loaded once per helpdesk page and kept fresh.

const Ctx = createContext(null);

export const useHd = () => useContext(Ctx);

export const HelpdeskProvider = ({ children }) => {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const reload = useCallback(async () => {
    try {
      setMeta(await hd.meta());
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the helpdesk"));
    }
  }, []);

  useEffect(() => {
    reload();
    getAgentSocket();
  }, [reload]);

  // Activity heartbeat (max once a minute) so auto-away only hits idle agents.
  useEffect(() => {
    let last = 0;
    const ping = () => {
      if (Date.now() - last < 60000) return;
      last = Date.now();
      getAgentSocket()?.emit("presence.activity");
    };
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((e) => window.addEventListener(e, ping, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, ping));
  }, []);

  // presence of every agent, including me
  useSocketEvent("agent.presence", (p) =>
    setMeta((m) =>
      m
        ? {
            ...m,
            me: String(m.me.id) === String(p.memberId) ? { ...m.me, presence: p.presence } : m.me,
            agents: m.agents.map((a) => (String(a.memberId) === String(p.memberId) ? { ...a, presence: p.presence } : a)),
          }
        : m
    )
  );
  useSocketEvent("presence.self", (p) => setMeta((m) => (m ? { ...m, me: { ...m.me, presence: p.presence } } : m)));

  const notify = useCallback((message, severity = "success") => setToast({ message, severity, key: Date.now() }), []);

  const value = useMemo(() => {
    if (!meta) return null;
    const perms = new Set(meta.me.permissions);
    const byId = (list) => new Map(list.map((x) => [String(x._id), x]));
    return {
      ...meta,
      can: (p) => perms.has(p),
      statusById: byId(meta.statuses),
      priorityById: byId(meta.priorities),
      categoryById: byId(meta.categories),
      teamById: byId(meta.teams),
      agentById: new Map(meta.agents.map((a) => [String(a.memberId), a])),
      reload,
      notify,
    };
  }, [meta, reload, notify]);

  return (
    <>
      {error && !meta ? (
        <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
      ) : !value ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
          <CircularProgress size={28} sx={{ color: T.brand }} />
        </Box>
      ) : (
        <Ctx.Provider value={value}>{children}</Ctx.Provider>
      )}
      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        {toast ? <Alert key={toast.key} severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </>
  );
};

/** Page frame for every helpdesk screen: CRM sidebar + metadata provider + top bar. */
export const HelpdeskPage = ({ children, maxWidth = "xl", flush = false }) => (
  <PageShell maxWidth={flush ? false : maxWidth}>
    <HelpdeskProvider>
      {!flush && <TopBar />}
      {children}
    </HelpdeskProvider>
  </PageShell>
);
